import { css } from "@emotion/css"
import { useContext, useState } from "react"

import Spacer from "@p-ban/common/src/components/layout/Spacer"
import useMutationApi from "@p-ban/common/src/core/api/useMutationApi"
import useMountEffect from "@p-ban/common/src/hooks/useMountEffect"
import { userValidator } from "@p-ban/common/src/logic/validation/userValidator"
import { validator } from "@p-ban/common/src/logic/validation/validationScheme"
import { toHankakuNumber, toKatakana } from "@p-ban/common/src/utils/StringUtils"

import boosterCouponApi from "../../../api/coupon/boosterCouponApi"
import ImportantDescriptionLink from "../../../components/ImportantDescriptionLink/ImportantDescriptionLink"
import Button from "../../../components/mypage/Button/Button"
import MyPageErrorMessages from "../../../components/mypage/MyPageErrorMessages"
import MyPageForm from "../../../components/mypage/MyPageForm"
import MyPageFormField from "../../../components/mypage/MyPageFormField"
import InputLabel from "../../../components/mypage/form/InputLabel"
import Select from "../../../components/mypage/form/Select"
import CheckBox from "../../../components/mypage/form/checkbox/CheckBox"
import TextInput from "../../../components/mypage/form/input/TextInput"
import Password from "../../../components/mypage/form/password/Password"
import AlignCenterHorizontally from "../../../components/mypage/layout/AlignCenterHorizontally"
import MyPageButtonContainer from "../../../components/mypage/layout/MyPageButtonContainer"
import SimpleGrid from "../../../components/mypage/layout/SimpleGrid"
import Link from "../../../components/ui/Link/Link"
import useForm from "../../../hooks/useForm"
import * as GoogleAnalytics from "../../../lib/GoogleAnalytics"
import useNextRouter from "../../../routes/useNextRouter"
import signUpApi from "../api/signUpApi"
import SignUpTemplate from "../components/SignUpTemplate"
import { SignUpFormContext } from "../context/SignUpFormContext"
import useSignUpCodeValueQueries from "../hooks/useSignUpCodeValueQueries"
import useViaReferralLink from "../hooks/useViaReferralLink"

/**
 * 画面更新をすると法人個人選択の状態がnullで入力フォームが表示されてしまうため、
 * その場合に法人個人選択画面へ自動遷移させます。
 */
const useBackToEntryOnReload = () => {
  const { values } = useContext(SignUpFormContext)

  const router = useNextRouter("/signup/entry")
  useMountEffect(() => {
    if (values.personalOrCorporate === null) {
      router.replace({ keepQuery: true })
    }
  })
}

const useTermsOfService = () => {
  const [isAgreedTermsOfService, setIsAgreedTermsOfService] = useState(false)

  return {
    termsOfServiceProps: {
      checked: isAgreedTermsOfService,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => setIsAgreedTermsOfService(e.target.checked),
    },
  }
}

const useSignUpForm = () => {
  const { values, setValues, setWasSignedUp } = useContext(SignUpFormContext)
  const isCorporate = values.personalOrCorporate?.isCorporate ?? false
  const { viaReferralLink } = useViaReferralLink()
  const { setFieldValue, getFieldProps, getFieldErrors, preSubmit } = useForm(values, setValues, {
    correctors: {
      corporateNameKana: toKatakana,
      phoneNumber: toHankakuNumber,
    },
    validators: {
      personalOrCorporate: validator.required,
      password: userValidator.password,
      corporateName: isCorporate ? userValidator.corporateName : undefined,
      corporateNameKana: isCorporate ? userValidator.corporateNameKana : undefined,
      userFamilyName: userValidator.familyName,
      userFirstName: userValidator.firstName,
      phoneNumber: userValidator.phoneNumber,
      userAdvertisingMediaCode: viaReferralLink ? undefined : validator.required,
      couponCode: (code) => {
        return code === ""
          ? undefined
          : boosterCouponApi.valid(code).then((result) => {
              return result.isSuccess ? undefined : ["無効なクーポンコードです。"]
            })
      },
    },
  })

  const router = useNextRouter("/signup/complete")
  const { isLoading, mutate, allErrorMessages } = useMutationApi(signUpApi.create, {
    onSuccess(result) {
      setWasSignedUp(true)
      // 旧フロー: GTMにユーザー登録完了イベントを送信（新フローのSignUpEntry.tsxと同様）
      GoogleAnalytics.sendCustomEvent("sign_up_complete", result.userId)
      setTimeout(() => {
        router.push({ keepQuery: true })
      }, 3000)
    },
  })

  const { termsOfServiceProps } = useTermsOfService()

  return {
    isLoading,
    loginEmail: values.loginEmail,
    isCorporate,
    viaReferralLink,
    getFieldProps,
    getServiceToUseProps(value: string) {
      return {
        name: "serviceToUse",
        onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
          const { value, checked } = event.target
          const nextValues = checked
            ? [...values.serviceToUse, value]
            : values.serviceToUse.filter((checkedValue) => checkedValue !== value)

          setFieldValue("serviceToUse", nextValues)
        },
        checked: values.serviceToUse.includes(value),
      }
    },
    serverErrors: allErrorMessages,
    getFieldErrors,

    termsOfServiceProps,
    backButtonProps: {
      onClick: () => window.history.back(),
    },
    submitButtonProps: {
      isLoading,
      disabled: !termsOfServiceProps.checked,
      onClick: async () => {
        const { canSubmit } = await preSubmit()
        if (canSubmit) {
          mutate({ ...values, viaReferralLink })
        }
      },
    },
  }
}

const SignUpForm = () => {
  useBackToEntryOnReload()
  const options = useSignUpCodeValueQueries()
  const {
    isLoading,
    loginEmail,
    isCorporate,
    viaReferralLink,
    getFieldProps,
    getFieldErrors,
    getServiceToUseProps,
    termsOfServiceProps,
    backButtonProps,
    submitButtonProps,
    serverErrors,
  } = useSignUpForm()

  return (
    <SignUpTemplate currentStep="入力フォーム" subTitle="お客様情報の入力">
      <MyPageForm layout="full" fieldWidth={518} gap={30}>
        <MyPageFormField label="メールアドレス（ログインID）" alignBaseLine={false}>
          {loginEmail}
          <Spacer size={10} />
          {/* ブラウザのパスワードマネージャー等にメールアドレスを認識させるために記述しています。 */}
          <input style={{ display: "none" }} autoComplete="username" defaultValue={loginEmail} />
        </MyPageFormField>
        <MyPageFormField
          label="パスワード"
          type="required"
          fieldDescription="（半角英大文字・小文字・数字・記号をそれぞれ1文字以上含む8文字以上）"
          passId
          errorMessages={getFieldErrors("password")}
        >
          <Password
            {...getFieldProps("password")}
            autoComplete="new-password"
            maxLength={256}
            inputWidth={265}
            readOnly={isLoading}
          />
        </MyPageFormField>
        {isCorporate && (
          <MyPageFormField
            label="会社名・学校名"
            type="required"
            fieldDescription="（25文字以内、半角カナ不可）"
            passId
            errorMessages={getFieldErrors("corporateName")}
          >
            <TextInput {...getFieldProps("corporateName")} autoComplete="organization" readOnly={isLoading} />
          </MyPageFormField>
        )}
        {isCorporate && (
          <MyPageFormField
            label="会社名・学校名（フリガナ）"
            type="required"
            fieldDescription="（40文字以内、半角カナ不可）"
            passId
            errorMessages={getFieldErrors("corporateNameKana")}
          >
            <TextInput {...getFieldProps("corporateNameKana")} autoComplete="off" readOnly={isLoading} />
          </MyPageFormField>
        )}
        <MyPageFormField
          label="氏名"
          type="required"
          fieldDescription="（8文字以内、半角カナ不可）"
          asGroup
          errorMessages={getFieldErrors("userFamilyName", "userFirstName")}
        >
          <div className={styles.userNameContainer}>
            <InputLabel label="姓">
              <TextInput {...getFieldProps("userFamilyName")} autoComplete="family-name" readOnly={isLoading} />
            </InputLabel>
            <InputLabel label="名">
              <TextInput {...getFieldProps("userFirstName")} autoComplete="given-name" readOnly={isLoading} />
            </InputLabel>
          </div>
        </MyPageFormField>
        <MyPageFormField label="電話番号" type="required" passId errorMessages={getFieldErrors("phoneNumber")}>
          <TextInput type="tel" {...getFieldProps("phoneNumber")} autoComplete="tel" readOnly={isLoading} />
        </MyPageFormField>
        {/* 友達紹介クーポンがある場合は知人からの紹介に固定して入力の手間を省く */}
        {!viaReferralLink && (
          <MyPageFormField
            label="本サービスを知ったきっかけ"
            type="required"
            passId
            errorMessages={getFieldErrors("userAdvertisingMediaCode")}
          >
            <Select
              options={options.userAdvertisingMediaCode}
              {...getFieldProps("userAdvertisingMediaCode")}
              disabled={isLoading}
            />
          </MyPageFormField>
        )}
        <MyPageFormField
          label={"利用したいサービス\n（複数回答可）"}
          asGroup
          alignBaseLine={false}
          errorMessages={getFieldErrors("serviceToUse")}
        >
          <SimpleGrid columns={2}>
            {options.serviceToUse.map(({ label, value }) => (
              <CheckBox key={value} {...getServiceToUseProps(value)} value={value} disabled={isLoading}>
                {label}
              </CheckBox>
            ))}
          </SimpleGrid>
        </MyPageFormField>
        <MyPageFormField
          label="クーポンコード（お友達紹介）"
          fieldDescription="（半角英数字）"
          errorMessages={getFieldErrors("couponCode")}
          render={({ id }) => (
            <div className={styles.couponCode.container}>
              <div className={styles.couponCode.input}>
                <TextInput id={id} {...getFieldProps("couponCode")} readOnly={viaReferralLink || isLoading} />
              </div>
              <Link href="/campaign/refer_friends.html" target="_blank" size="small">
                お友達紹介とは
              </Link>
            </div>
          )}
        />
      </MyPageForm>
      <Spacer size={40} />
      <AlignCenterHorizontally>
        <ImportantDescriptionLink
          href="/information/terms_of_service.html"
          theme="blue"
          description="ご利用規約を"
          highlightedDescription="読む"
        />
        <Spacer size={20} />
        <CheckBox {...termsOfServiceProps} direction="row-reverse">
          ご利用規約に同意する
        </CheckBox>
        <Spacer size={20} />
        <MyPageErrorMessages>{serverErrors}</MyPageErrorMessages>
        <Spacer size={20} />
        <MyPageButtonContainer>
          <Button inverse padding="short" {...backButtonProps}>
            戻る
          </Button>
          <Button {...submitButtonProps}>登録する</Button>
        </MyPageButtonContainer>
      </AlignCenterHorizontally>
    </SignUpTemplate>
  )
}

export default SignUpForm

const styles = {
  userNameContainer: css`
    display: flex;
    column-gap: 10px;
  `,
  couponCode: {
    container: css`
      display: flex;
      column-gap: 15px;
      align-items: center;
    `,
    input: css`
      width: 265px;
    `,
  },
}
